import { Injectable, computed, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, filter, map, take } from 'rxjs';
import { AuthService } from '../auth/auth';
import { StateStoreService } from '../state-store/state-store';

/**
 * Single source of truth for "the app has finished its boot sequence".
 *
 * Ready means: the initial auth probe has settled, and — if the user turned
 * out to be authenticated — the initial state load (`whoami` + org list +
 * membership) has completed. Anonymous users are considered ready as soon
 * as auth has settled, since they have no state to load.
 *
 * Consumed by `appReadyMatch` so route guards downstream of `canMatch` can
 * read state synchronously without re-implementing readiness waits.
 */
@Injectable({ providedIn: 'root' })
export class AppReadyService {
  private readonly auth = inject(AuthService);
  private readonly state = inject(StateStoreService);

  readonly isReady = computed(() => {
    if (this.auth.isLoading()) return false;
    if (!this.auth.isAuthenticated()) return true;
    return this.state.isStateReady();
  });

  /** Emits `true` exactly once, the first moment `isReady` becomes true. */
  readonly ready$: Observable<true> = toObservable(this.isReady).pipe(
    filter(Boolean),
    take(1),
    map(() => true as const)
  );
}
